import { useMemo } from 'react';

import { EditorPresetBuilder } from '@atlaskit/editor-common/preset';
import { createFeatureFlagsFromProps } from '@atlaskit/editor-core';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import { tasksAndDecisionsPlugin } from '@atlaskit/editor-plugin-tasks-and-decisions';
import { alignmentPlugin } from '@atlaskit/editor-plugins/alignment';
import { analyticsPlugin } from '@atlaskit/editor-plugins/analytics';
import { annotationPlugin } from '@atlaskit/editor-plugins/annotation';
import { basePlugin } from '@atlaskit/editor-plugins/base';
import { betterTypeHistoryPlugin } from '@atlaskit/editor-plugins/better-type-history';
import { blockTypePlugin } from '@atlaskit/editor-plugins/block-type';
import { borderPlugin } from '@atlaskit/editor-plugins/border';
import { breakoutPlugin } from '@atlaskit/editor-plugins/breakout';
import { captionPlugin } from '@atlaskit/editor-plugins/caption';
import { cardPlugin } from '@atlaskit/editor-plugins/card';
import { clearMarksOnEmptyDocPlugin } from '@atlaskit/editor-plugins/clear-marks-on-empty-doc';
import { clipboardPlugin } from '@atlaskit/editor-plugins/clipboard';
import { codeBlockPlugin } from '@atlaskit/editor-plugins/code-block';
import { compositionPlugin } from '@atlaskit/editor-plugins/composition';
import { contentInsertionPlugin } from '@atlaskit/editor-plugins/content-insertion';
import { contextIdentifierPlugin } from '@atlaskit/editor-plugins/context-identifier';
import { contextPanelPlugin } from '@atlaskit/editor-plugins/context-panel';
import { copyButtonPlugin } from '@atlaskit/editor-plugins/copy-button';
import { datePlugin } from '@atlaskit/editor-plugins/date';
import { decorationsPlugin } from '@atlaskit/editor-plugins/decorations';
import { editorDisabledPlugin } from '@atlaskit/editor-plugins/editor-disabled';
import { emojiPlugin } from '@atlaskit/editor-plugins/emoji';
import { expandPlugin } from '@atlaskit/editor-plugins/expand';
import { extensionPlugin } from '@atlaskit/editor-plugins/extension';
import { featureFlagsPlugin } from '@atlaskit/editor-plugins/feature-flags';
import { floatingToolbarPlugin } from '@atlaskit/editor-plugins/floating-toolbar';
import { focusPlugin } from '@atlaskit/editor-plugins/focus';
import { gridPlugin } from '@atlaskit/editor-plugins/grid';
import { guidelinePlugin } from '@atlaskit/editor-plugins/guideline';
import { highlightPlugin } from '@atlaskit/editor-plugins/highlight';
import { historyPlugin } from '@atlaskit/editor-plugins/history';
import { hyperlinkPlugin } from '@atlaskit/editor-plugins/hyperlink';
import { indentationPlugin } from '@atlaskit/editor-plugins/indentation';
import { insertBlockPlugin } from '@atlaskit/editor-plugins/insert-block';
import { layoutPlugin } from '@atlaskit/editor-plugins/layout';
import { listPlugin } from '@atlaskit/editor-plugins/list';
import { mediaPlugin } from '@atlaskit/editor-plugins/media';
import { mediaInsertPlugin } from '@atlaskit/editor-plugins/media-insert';
import { mentionsPlugin } from '@atlaskit/editor-plugins/mentions';
import { panelPlugin } from '@atlaskit/editor-plugins/panel';
import { pastePlugin } from '@atlaskit/editor-plugins/paste';
import { placeholderPlugin } from '@atlaskit/editor-plugins/placeholder';
import { placeholderTextPlugin } from '@atlaskit/editor-plugins/placeholder-text';
import { primaryToolbarPlugin } from '@atlaskit/editor-plugins/primary-toolbar';
import { quickInsertPlugin } from '@atlaskit/editor-plugins/quick-insert';
import { rulePlugin } from '@atlaskit/editor-plugins/rule';
import { selectionPlugin } from '@atlaskit/editor-plugins/selection';
import { selectionToolbarPlugin } from '@atlaskit/editor-plugins/selection-toolbar';
import { statusPlugin } from '@atlaskit/editor-plugins/status';
import { submitEditorPlugin } from '@atlaskit/editor-plugins/submit-editor';
import { tablesPlugin } from '@atlaskit/editor-plugins/table';
import { textColorPlugin } from '@atlaskit/editor-plugins/text-color';
import { textFormattingPlugin } from '@atlaskit/editor-plugins/text-formatting';
import { toolbarListsIndentationPlugin } from '@atlaskit/editor-plugins/toolbar-lists-indentation';
import { typeAheadPlugin } from '@atlaskit/editor-plugins/type-ahead';
import { undoRedoPlugin } from '@atlaskit/editor-plugins/undo-redo';
import { unsupportedContentPlugin } from '@atlaskit/editor-plugins/unsupported-content';
import { widthPlugin } from '@atlaskit/editor-plugins/width';
import { EmojiResource } from '@atlaskit/emoji';
import { toolbarPlugin } from '@atlaskit/editor-plugin-toolbar';
import { localIdPlugin } from '@atlaskit/editor-plugins/local-id';

import { useLinkPickerEditorProps } from '@atlassian/link-picker-plugins/editor';

import { useEditorFeatureFlags } from '@confluence/editor-features/entry-points/useEditorFeatureFlags.ts';
import { legacyContentMacroPlugin } from '@confluence/experiment-legacy-content-macro-plugin';
import { createEditorMediaProvider } from '@confluence/fabric-media-support/entry-points/MediaProvider.ts';
import { getTaskDecisionProvider } from '@confluence/fabric-providers/entry-points/TaskDecisionProvider.ts';
import { getEmojiProvider } from '@confluence/fabric-providers/entry-points/EmojiProvider.ts';
import { useProfilecardProvider } from '@confluence/fabric-providers/entry-points/useProfileCardProvider.ts';
import { fg } from '@confluence/feature-gating';
import { useObjectSidebarActions } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { useContentType, usePageContentId } from '@confluence/page-context';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useEditorAIOptIn } from '@confluence/editor-features/entry-points/useEditorAIOptIn.ts';

import { decoratePlugin } from './decorate-plugin.ts';

// Below are the experiments that are enabled in the inner editor. We're using an opt-in approach, so they have to be explictly referenced here.
// This is to avoid any unexpected changes from experiments that are untested against the LCM.
const allowedExperiments = { 'macro-interaction-updates': true };

// Below are flags hardcoded for the LCM editor. These flags are not available in Switcheroo.
const lcmExclusiveFlags = {
	'lcm-prevent-render-tracking': true, // Used in editor-internal & ReactEditorViewNext to disable render tracking. See - https://product-fabric.atlassian.net/browse/ED-26650
};

interface UseNBMEditorPresetOptions {
	nodeId?: string;
	annotationProvider?: any;
}

export function useNBMEditorPreset({ nodeId, annotationProvider }: UseNBMEditorPresetOptions): {
	preset: EditorPresetBuilder<any, any>;
	editorApi: any;
	fullPageEditorFeatureFlags: Record<string, boolean | string>;
} {
	const isFullPage = true;
	const { cloudId, userId, isAdminHubAIEnabled, edition, orgId, environment } = useSessionData();

	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	const { push } = useRouteActions();
	const { optInStatus } = useEditorAIOptIn({
		isAdminHubAIEnabled,
		edition,
		orgId,
		environment,
	});

	const editorFeatureFlags = useEditorFeatureFlags();

	const fullPageEditorFeatureFlags = useMemo(
		() => ({
			...editorFeatureFlags,
			...allowedExperiments,
			...lcmExclusiveFlags,
		}),
		[editorFeatureFlags],
	);

	const profilecardProvider = useProfilecardProvider({
		push,
		cloudId,
		featureFlags: fullPageEditorFeatureFlags,
		userId,
	});
	const { emojiProvider, mediaProvider, taskDecisionProvider } = useMemo(() => {
		const emojiProvider = getEmojiProvider(EmojiResource, {
			cloudId,
			userId,
		});
		const mediaProvider = createEditorMediaProvider({
			contentId: contentId || '',
			contentType: contentType || '',
			isExternalShare: false,
		});
		const taskDecisionProvider = getTaskDecisionProvider();
		return { emojiProvider, mediaProvider, taskDecisionProvider };
	}, [userId, cloudId, contentId, contentType]);

	const productConfig = useMemo(
		() => ({
			product: 'confluence',
			cloudId,
		}),
		[cloudId],
	);

	const linkPicker = useLinkPickerEditorProps(undefined, productConfig);

	const { showObjectSidebar, hideObjectSidebar, hideObjectSidebarByPanelId } =
		useObjectSidebarActions();

	const toolbarPluginEnabled =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		fg('aifc_create_enabled') ||
		(optInStatus !== 'enabled' &&
			expValEquals('platform_editor_toolbar_aifc_confluence', 'isEnabled', true));

	const finalPreset = new EditorPresetBuilder()
		// Default preset minus blockType
		.add([featureFlagsPlugin, createFeatureFlagsFromProps(fullPageEditorFeatureFlags) || {}])
		.add([
			analyticsPlugin,
			{
				// TODO: renable when we have props passed in for these
				// createAnalyticsEvent: options.createAnalyticsEvent,
				// performanceTracking: options.performanceTracking,
			},
		])
		.add(betterTypeHistoryPlugin)
		.add([pastePlugin, { isFullPage }])
		.add(clipboardPlugin)
		.add(focusPlugin)
		.add(compositionPlugin)
		.add([
			contextIdentifierPlugin,
			{
				// TODO: renable when we have props passed in for these
				// contextIdentifierProvider: options.contextIdentifierProvider
			},
		])
		.add([basePlugin, {}])
		.maybeAdd(localIdPlugin, fg('platform_editor_lce_localid_plugin'))
		.add(decorationsPlugin)
		.add([typeAheadPlugin, {}])
		.add(historyPlugin)
		.maybeAdd([toolbarPlugin, { disableSelectionToolbar: true }], toolbarPluginEnabled)
		.add([primaryToolbarPlugin, { contextualFormattingEnabled: true }])
		.add(undoRedoPlugin)
		.add(clearMarksOnEmptyDocPlugin)
		.add([
			selectionToolbarPlugin,
			{
				contextualFormattingEnabled: true,
			},
		])
		.add([
			hyperlinkPlugin,
			{
				linkPicker,
				// @ts-ignore Temporary solution to check for Live Page editor.
				__livePage: false,
			},
		])
		.add([
			decoratePlugin(
				textFormattingPlugin,
				[],
				{},
				// Remove mark exclusion between code and all other text formatting (fontStyle), links, type-ahead (searchQuery) and background/text color (color)
				new Map([['code', (markSpec) => ({ ...markSpec, excludes: undefined })]]),
			),
			{},
		])
		.add(widthPlugin)
		.add([quickInsertPlugin, { enableElementBrowser: true }])
		.add([
			placeholderPlugin,
			{
				// The placeholder plugin doens't initiliase if there isn't at least a placeholder, and it gets used to render the editor controls placeholder copy too
				placeholder: ' ',
			},
		])
		.add(unsupportedContentPlugin)
		.add(editorDisabledPlugin)
		.add(submitEditorPlugin)
		.add(copyButtonPlugin)
		.add(floatingToolbarPlugin)
		.add([selectionPlugin, { __livePage: false }])
		.add([codeBlockPlugin, {}])
		.add([
			contextPanelPlugin,
			{
				objectSideBar: {
					showPanel: showObjectSidebar,
					closePanel: hideObjectSidebar,
					closePanelById: hideObjectSidebarByPanelId,
				},
			},
		])
		// Decorated node support
		.add([decoratePlugin(expandPlugin, ['expand', 'nestedExpand']), { allowInsertion: true }])
		.add([insertBlockPlugin, { allowTables: true, allowExpand: true }])
		.add([
			decoratePlugin(extensionPlugin, ['bodiedExtension'], {
				transformer: (node) => ({
					...node,
					// Filter out node that skips nested bodied extension parsing
					parseDOM: node.parseDOM?.filter((i) => !i.skip && i.context !== 'bodiedExtension//'),
				}),
			}),
			{
				breakoutEnabled: false,
				appearance:
					expValEquals('editor_tinymce_full_width_mode', 'isEnabled', true) ||
					expValEquals('confluence_max_width_content_appearance', 'isEnabled', true)
						? 'max'
						: 'full-width',
			},
		])
		.add(captionPlugin)
		.add([
			mentionsPlugin,
			{
				sanitizePrivateContent: true,
				allowZeroWidthSpaceAfter: true,
				insertDisplayName: true,
				profilecardProvider,
			},
		])
		.add([emojiPlugin, { emojiProvider }])
		.add([
			decoratePlugin(panelPlugin, ['panel']),
			{ allowCustomPanel: true, allowCustomPanelEdit: true },
		])
		.add(contentInsertionPlugin)
		.add(guidelinePlugin)
		.add([
			decoratePlugin(tablesPlugin, ['tableCell', 'tableHeader', 'nestedExpand']),
			{
				tableOptions: {
					advanced: true,
					allowMergeCells: true,
					allowColumnResizing: true,
					allowNumberColumn: true,
					allowDistributeColumns: true,
				},
				allowContextualMenu: true,
				isTableScalingEnabled: true,
			},
		])
		.add(decoratePlugin(listPlugin, ['listItem']))
		.add([
			decoratePlugin(blockTypePlugin, ['blockquote']),
			{ includeBlockQuoteAsTextstyleOption: true },
		])
		.add(breakoutPlugin)
		.add(gridPlugin)
		.add([annotationPlugin, annotationProvider])
		.add([
			mediaPlugin,
			{
				provider: mediaProvider,
				allowLazyLoading: true,
				allowAdvancedToolBarOptions: true,
				allowMediaSingleEditable: true,
				allowRemoteDimensionsFetch: true,
				allowDropzoneDropLine: true,
				allowImagePreview: true,
				fullWidthEnabled: true,
				editorAppearance:
					expValEquals('editor_tinymce_full_width_mode', 'isEnabled', true) ||
					expValEquals('confluence_max_width_content_appearance', 'isEnabled', true)
						? 'max'
						: 'full-width',
				isCopyPasteEnabled: true,
				allowMediaSingle: true,
				allowPixelResizing: true,
				allowResizing: true,
				allowResizingInTables: true,
				allowLinking: true,
				allowAltTextOnImages: true,
				allowCaptions: true,
				allowMediaInlineImages: true,
				featureFlags: {
					mediaInline: true,
				},
			},
		])
		.add(mediaInsertPlugin)
		.add(alignmentPlugin)
		.add(indentationPlugin)
		.add([
			toolbarListsIndentationPlugin,
			{
				showIndentationButtons: true,
				allowHeadingAndParagraphIndentation: true,
			},
		])
		.add([
			expValEquals('platform_editor_blocktaskitem_patch_1', 'isEnabled', true)
				? decoratePlugin(tasksAndDecisionsPlugin, ['blockTaskItem'])
				: tasksAndDecisionsPlugin,
			{
				allowNestedTasks: true,
				taskDecisionProvider,
				consumeTabs: isFullPage,
				allowBlockTaskItem: true,
			},
		])
		.add(textColorPlugin)
		.add([placeholderTextPlugin, { allowInserting: false }])
		.add([
			layoutPlugin,
			{
				allowBreakout: true,
				UNSAFE_addSidebarLayouts: true,
				useLongPressSelection: false,
				UNSAFE_allowSingleColumnLayout: undefined,
				editorAppearance:
					expValEquals('editor_tinymce_full_width_mode', 'isEnabled', true) ||
					expValEquals('confluence_max_width_content_appearance', 'isEnabled', true)
						? 'max'
						: 'full-width',
			},
		])
		.add(datePlugin)
		.add([
			cardPlugin,
			{
				fullWidthMode: true,
				editorAppearance:
					expValEquals('editor_tinymce_full_width_mode', 'isEnabled', true) ||
					expValEquals('confluence_max_width_content_appearance', 'isEnabled', true)
						? 'max'
						: 'full-width',
				allowEmbeds: true,
			},
		])
		.add(statusPlugin)
		.add(rulePlugin)
		.add(
			decoratePlugin(
				highlightPlugin,
				[],
				{},
				// Remove mark exclusion between backgroundColor and textColor (color)
				new Map([['backgroundColor', (markSpec) => ({ ...markSpec, excludes: undefined })]]),
			),
		)
		// We need the LCM plugin on the inner LCM editor because it blocks dropping
		// LCMs into other LCMs with a drop handler and captures some edge case
		// keyboard navigation cases when exiting the LCM through a gap cursor
		.add([legacyContentMacroPlugin, { nodeId }])
		.maybeAdd(borderPlugin, fg('platform_editor_lce_borderplugin'));

	const { editorApi, preset } = usePreset(() => finalPreset);

	return { preset, editorApi, fullPageEditorFeatureFlags };
}
